import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Shield, TrendingUp, Sparkles } from "lucide-react";

const Hero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Animated background effects */}
      <div className="grid-background" />
      
      {/* Glowing orbs */}
      <div className="hero-gradient-orb blue" />
      <div className="hero-gradient-orb purple" />
      <div className="hero-gradient-orb cyan" />
      
      {/* Mouse-following spotlight - hidden on mobile */}
      <div 
        className="pointer-events-none fixed w-[500px] h-[500px] rounded-full opacity-20 hidden md:block"
        style={{
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
          left: mousePosition.x - 250,
          top: mousePosition.y - 250,
          transition: 'left 0.3s ease-out, top 0.3s ease-out',
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-12 md:py-32 relative z-10">
        <motion.div 
          className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Left content */}
          <motion.div
            variants={itemVariants}
            className="lg:col-span-7 text-left"
          >
            {/* Animated Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-2 mb-8 group cursor-pointer hover:bg-blue-500/20 transition-all duration-300"
            >
              <Sparkles size={16} className="text-blue-400 animate-pulse" />
              <span className="text-sm text-blue-300 font-medium">Enterprise Software Solutions</span>
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <ArrowRight size={14} className="text-blue-400" />
              </motion.div>
            </motion.div>

            {/* Headline with gradient text */}
            <motion.h1 
              variants={itemVariants}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-tight mb-6"
            >
              Powering Call Centers with{" "}
              <span className="text-gradient">Smart</span>
              <br />
              <span className="text-gradient">Software Solutions</span>
            </motion.h1>

            {/* Tagline with stagger animation */}
            <motion.p 
              variants={itemVariants}
              className="text-lg md:text-xl text-slate-400 max-w-2xl mb-10 leading-relaxed"
            >
              We build scalable, intelligent, and automation-driven systems that transform 
              how call centers and BPO companies operate. From CRM to workflow automation, 
              we deliver excellence.
            </motion.p>

            {/* CTA Buttons with enhanced animations */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4">
              <motion.a
                href="#contact"
                onClick={(e) => scrollToSection(e, "#contact")}
                whileHover={{ scale: 1.05, boxShadow: "0 0 50px rgba(59, 130, 246, 0.8)" }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white rounded-full px-8 py-4 font-medium transition-all duration-300 btn-glow ripple relative overflow-hidden group"
                data-testid="hero-cta-consultation"
              >
                <span className="relative z-10">Get a Free Consultation</span>
                <motion.div
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  <ArrowRight size={18} className="relative z-10" />
                </motion.div>
              </motion.a>
              <motion.a
                href="#services"
                onClick={(e) => scrollToSection(e, "#services")}
                whileHover={{ scale: 1.05, backgroundColor: "rgba(30, 41, 59, 0.8)" }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center justify-center gap-2 bg-transparent border border-slate-700 text-white hover:border-blue-500/50 rounded-full px-8 py-4 font-medium transition-all duration-300 relative overflow-hidden group"
                data-testid="hero-cta-solutions"
              >
                <span>View Our Solutions</span>
                <Zap size={18} className="group-hover:text-blue-400 transition-colors" />
              </motion.a>
            </motion.div>

            {/* Animated Stats */}
            <motion.div
              variants={itemVariants}
              className="flex flex-wrap gap-8 mt-16"
            >
              {[
                { number: "50+", label: "Projects Delivered" },
                { number: "99%", label: "Client Satisfaction" },
                { number: "24/7", label: "Technical Support" },
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  className="stat-card group cursor-pointer"
                  whileHover={{ scale: 1.1, y: -5 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                >
                  <motion.div 
                    className="stat-number"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1 + index * 0.1, type: "spring", stiffness: 200 }}
                  >
                    {stat.number}
                  </motion.div>
                  <div className="text-sm text-slate-400 group-hover:text-slate-300 transition-colors">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right content - Animated Feature cards */}
          <motion.div
            variants={itemVariants}
            className="lg:col-span-5 hidden lg:block"
          >
            <div className="relative">
              {/* Floating feature cards */}
              <motion.div
                animate={{ 
                  y: [0, -15, 0],
                  rotateZ: [0, 1, 0],
                }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                whileHover={{ scale: 1.05, rotateZ: 2 }}
                className="glass-card p-6 mb-6 cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <motion.div 
                    className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Shield className="text-blue-400" size={24} />
                  </motion.div>
                  <div>
                    <h3 className="font-semibold text-white">Secure Systems</h3>
                    <p className="text-sm text-slate-400">Enterprise-grade security</p>
                  </div>
                </div>
                {/* Animated progress bar */}
                <div className="mt-4 h-1 bg-slate-800 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-blue-500 to-cyan-500"
                    initial={{ width: 0 }}
                    animate={{ width: "85%" }}
                    transition={{ delay: 1.5, duration: 1, ease: "easeOut" }}
                  />
                </div>
              </motion.div>

              <motion.div
                animate={{ 
                  y: [0, -12, 0],
                  rotateZ: [0, -1, 0],
                }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                whileHover={{ scale: 1.05, rotateZ: -2 }}
                className="glass-card p-6 mb-6 ml-12 cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <motion.div 
                    className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.5 }}
                  >
                    <TrendingUp className="text-purple-400" size={24} />
                  </motion.div>
                  <div>
                    <h3 className="font-semibold text-white">Scalable Solutions</h3>
                    <p className="text-sm text-slate-400">Grow without limits</p>
                  </div>
                </div>
                {/* Animated dots */}
                <div className="mt-4 flex gap-2">
                  {[...Array(5)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="w-2 h-2 rounded-full bg-purple-500"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 1.8 + i * 0.1 }}
                    />
                  ))}
                </div>
              </motion.div>

              <motion.div
                animate={{ 
                  y: [0, -18, 0],
                  rotateZ: [0, 1.5, 0],
                }}
                transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                whileHover={{ scale: 1.05, rotateZ: 3 }}
                className="glass-card p-6 cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <motion.div 
                    className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Zap className="text-cyan-400" size={24} />
                  </motion.div>
                  <div>
                    <h3 className="font-semibold text-white">Fast Deployment</h3>
                    <p className="text-sm text-slate-400">Quick time to market</p>
                  </div>
                </div>
                {/* Lightning animation */}
                <motion.div
                  className="mt-4 text-cyan-400 text-xs font-mono"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  {">>> Deploying in 3...2...1... ✓"}
                </motion.div>
              </motion.div>

              {/* Floating decorative elements */}
              <motion.div
                className="absolute -top-10 -right-10 w-20 h-20 border border-blue-500/20 rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              />
              <motion.div
                className="absolute -bottom-5 -left-5 w-16 h-16 border border-purple-500/20 rounded-full"
                animate={{ rotate: -360 }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              />
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-slate-600 rounded-full flex justify-center pt-2">
          <motion.div
            className="w-1.5 h-3 bg-blue-500 rounded-full"
            animate={{ y: [0, 12, 0], opacity: [1, 0, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
